import Vue from 'vue'
import { EventServiceInstance } from '@/services/EventService'
// EventConfigDB,
import { EventConfigForm } from '@/models/EventConfig'
import { isObjectEmpty } from '@/utils/isObjectEmpty'
import { formErrorsHandler } from '@/utils/formErrorsHandler'
import { axiosDownloadable } from '@/utils/axiosDownloadable'
import { ElementTypeKeys } from '@/enums/ElementTypeKeys'
import { isHappening } from '@/utils/dateHelpers'
import { VideoKeys } from '@/enums/VideoKeys'
import { EventNotificationsInstance } from '@/services/EventNotifications'
import { AccessControlsServiceInstance } from '@/services/AccessControlsService'
import { quickNotesServiceInstance } from '@/services/quickNotesService'
import { EventHybridServiceInstance } from '@/services/EventHybridService'
import { RegistriesCalendarsService } from '@/services/RegistriesCalendarsService'
import { CalendarRequestService } from '@/services/RegistriesCalendarsRequestService'
import { CalendarsItemsService } from '@/services/CalendarsItemsService'
import { debounce } from 'lodash'
import { AppCoreVitalKeys } from '@/enums/AppCoreVitalKeys'
// import { EventNavItemBD } from "@/models/EventNavItem";
export const helper = {
  IS_TOUCH_DEVICE: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
}
// Shared query params for the lazy roster endpoint (search + active/location/reps/
// hybrid filters + the current room slug for the location/reps filters).
const rosterParams = (state) => ({
  search: state.roster.search || undefined,
  filters: state.roster.filters,
  slug: (state.show && state.show.data && state.show.data.slug) || undefined,
})
const rosterQueryKey = (state) => JSON.stringify([
  state.constants.registry.uuid,
  rosterParams(state),
])

const fetchRosterAfterSearch = debounce(({ dispatch, state }, generation) => {
  if (state.roster.generation === generation) dispatch('fetchRosterFirstPage')
}, 300)

async function fetchRosterPage({ commit, state, dispatch }, { append = false } = {}) {
  if (append && (!state.roster.hasMore || state.roster.loading)) return
  if (!append) commit('invalidateRoster')

  const generation = state.roster.generation
  const queryKey = rosterQueryKey(state)
  const isCurrent = () => state.roster.generation === generation && rosterQueryKey(state) === queryKey
  commit('setRoster', { loading: true })

  try {
    const { data } = await EventServiceInstance.getRoster({
      params: { ...rosterParams(state), cursor: append ? state.roster.cursor : undefined },
    })
    if (!isCurrent()) return

    if (append) commit('appendRoster', data.data || [])
    else commit('setRoster', { items: data.data || [] })
    commit('setRoster', { cursor: data.next_cursor || null, hasMore: !!data.next_cursor })
  } catch (err) {
    if (!isCurrent()) return
    // Expired or pre-deployment cursors must restart, never change sort order.
    if (append && err.response && err.response.status === 409) {
      return dispatch('fetchRosterFirstPage')
    }
    throw formErrorsHandler(err)
  } finally {
    if (isCurrent()) commit('setRoster', { loading: false })
  }
}

const ATTENDEE_COUNT_ATTEMPTS = 3
const ATTENDEE_COUNT_RETRY_MS = 1000
// Presence outlives any one roster page. Apply it at every user-ingestion path
// so a delayed HTTP response cannot restore a stale database online flag.
const withOnlinePresence = (state, user) => state.socket.onlineUsers === null
  ? user
  : { ...user, is_online: state.socket.onlineUsers[user.id] === true }

// Una sola persona entró o salió: se reemplaza solo su fila.
//
// refreshOnlinePresence reconstruye socket.users, roster.items y userCache
// enteros con una copia nueva de cada objeto. Para la foto inicial o una
// reconexión está bien, pero se llamaba en CADA entrada/salida: con ~1.100
// usuarios eran ~15 ms por evento en una máquina rápida (Vue 2 vuelve
// reactivo cada objeto nuevo y redibuja todas las listas que lo muestran).
// Al arrancar un evento llegan decenas por segundo y el chat se congelaba.
const refreshUserPresence = (state, id) => {
  const key = String(id)
  const patch = (list) => {
    const index = list.findIndex((user) => String(user.id) === key)
    if (index !== -1) {
      list.splice(index, 1, withOnlinePresence(state, list[index]))
    }
  }
  patch(state.socket.users)
  patch(state.roster.items)
  const cached = Object.entries(state.roster.userCache)
    .find(([, user]) => String(user.id) === key)
  if (cached) {
    Vue.set(state.roster.userCache, cached[0], withOnlinePresence(state, cached[1]))
  }
}

const refreshOnlinePresence = (state) => {
  state.socket.users = state.socket.users.map((user) => withOnlinePresence(state, user))
  state.roster.items = state.roster.items.map((user) => withOnlinePresence(state, user))
  state.roster.userCache = Object.fromEntries(
    Object.entries(state.roster.userCache).map(([uuid, user]) => [uuid, withOnlinePresence(state, user)]),
  )
}
const currentContentLibraryElementId = (state, getters) => {
  if (state.contentItem.contentItemModal) {
    return state.contentItem.contentItemData.id
  }
  if (state.contentVideo.contentVideoModal) {
    return state.contentVideo.contentVideoData.id
  }
  if (getters.emebedContentElement) {
    return getters.emebedContentElement.id
  }
  return null
}
// state
const initState = () => {
  return {
    constants: {
      users_loading: false,
      attendees_count: null,
      attendees_count_generation: 0,
      // Last people-per-event/room counts from /event/constants, until the
      // first LocationCounts broadcast. null = the backend had none yet.
      location_counts: null,
      needs_refershing: false,
      needs_hybrid_refershing: false,
      is_user_in_event: false,
      is_preview: true,
      is_desktop: true,
      room_width: 1680,
      room_height: 1050,
      window_width: 1680,
      window_height: 1050,
      constantsLoaded: false,
      // Donovann
      threads: [],
      channels: [],
      notifications: [],
      // End Donovann
      event_configs: {
        data: {
          current_config_language_key: 'es',
          es: EventConfigForm(),
          en: EventConfigForm(),
        },
      },
      nav_items: {
        data: {
          es: [],
          en: [],
        },
      },
      virtual_rooms: [],
      rep_rooms: [],
      registry: {
        forms: [],
        configs: [],
        nav_items: [],
      },
      license: {},
      event_connections: [],
      event_users_block: [],
      profiles: [],
      favorites: [],
      nav_count: 0,
      uuid_tracker: null,
      navigation_log_virtual_room_ids: [],
      version: '1.0.0',
      quick_notes: [],
    },
    flexible: {
      event_calendar_modal: false,
    },
    iframeItem: {
      iframeItemModal: false,
      iframeItemUrl: '',
      iframeItemHtml: '',
      iframeItemType: 'link',
    },
    simpleVideoItem: {
      simpleVideoItemModal: false,
      simpleVideoItemUrl: '',
      simpleVideoItemType: 'youtube',
    },
    contentItem: {
      contentItemModal: false,
      contentItemData: {},
    },
    contentVideo: {
      contentVideoModal: false,
      contentVideoData: {},
    },
    activeInmediatePopUp: {
      contentVideoModal: false,
    },
    contentWindow: {
      contentWindowModal: false,
      contentWindowData: {
        content_item_elements: [],
      },
    },
    search: {
      modal: false,
    },
    card: {
      modal: false,
      attendee: null,
    },
    photobooth: {
      photo_booth_config_is_loading: false,
      photo_booth_config: { backgrounds: [] },
      photobooth_config_id: null,
      gallery_modal: false,
      modal: false,
    },
    profile: {
      tab: 'profile',
      modal: false,
      attendee: null,
    },
    rep_dashboard: {
      tab: 'in_stand',
      currentSelectedSlug: '',
      modal: false,
      attendee: null,
    },
    mod_dashboard: {
      tab: 'attendees',
      modal: false,
    },
    mod_dashboard_custom_form: {
      tab: 'attendees',
      modal: false,
    },
    userProfile: {
      isUpdating: false,
    },
    userCards: {
      isExporting: false,
    },
    socket: {
      onlyThisLocation: false,
      onlyActive: false,
      filters: [],
      globalChannels: [],
      users: [],
      usersLocation: [],
      // People per event and per room, broadcast by the backend every few
      // seconds ({ [registry_uuid]: { total, rooms: { [slug]: n } } }). null =
      // none received; the counters then fall back to `usersLocation`.
      locationCounts: null,
      // null = no live snapshot yet; {} = successfully connected, nobody online.
      onlineUsers: null,
      // Whether the FULL attendee roster has been loaded into `users`. Privileged
      // staff load it eagerly; everyone else only on first need (caveat 1).
      usersLoaded: false,
    },
    // Lazy roster (users-list-plan.md): server-paginated list the chat UsersList
    // infinite-scrolls. `userCache` backs on-demand uuid lookups so realtime
    // events (DM toasts, add-contact) resolve identities without the full array.
    roster: {
      generation: 0,
      items: [],
      cursor: null,
      hasMore: true,
      loading: false,
      search: '',
      filters: [],
      userCache: {},
    },
    show: {
      isLoading: true,
      data: {
        channels: [],
        elements: [],
        reps: [],
      },
    },
    customform: {
      modal: false,
      form: {},
    },
  }
}

// getters
const getters = {
  currentBreakPointKey(state) {
    return state.constants.is_desktop ? 'desktop' : 'mobile'
  },
  canOpenModModal(state, getters, rootState, rootGetters) {
    const { is_hybrid } = state.constants.registry
    const validRole = [
      rootGetters['session/isAdmin'],
      rootGetters['session/isModerator'],
    ].includes(true)
    return is_hybrid && validRole
  },
  // Modo privacidad (registries.disable_real_time, nombre engañoso): el
  // asistente no ve a otros asistentes ni chatea con ellos; el personal sí,
  // para atender. Espejo de App\Services\AttendeePrivacy::canSeeAttendees —
  // el backend es quien lo hace cumplir; esto solo evita mostrar UI que el
  // backend va a rechazar. El soporte del evento cuenta como personal aunque
  // no tenga el rol.
  canSeeAttendees(state, getters, rootState) {
    const { disable_real_time, support_user_id } = state.constants.registry
    if (!disable_real_time) {
      return true
    }
    const user = rootState.session.user
    return getters.isPrivileged || (!!user && support_user_id === user.id)
  },
  // Nombre heredado, mismo criterio. Antes exceptuaba solo al admin, así que
  // con privacidad activa el rep perdía los chats y no podía atender soporte.
  isRealtimeEnabled(state, getters) {
    return getters.canSeeAttendees
  },
  // Staff who use the mod/rep dashboards (which need the whole roster). Only these
  // users eager-load every attendee; everyone else goes lazy (caveat 1 / Phase 4).
  isPrivileged(state, getters, rootState, rootGetters) {
    return [
      rootGetters['session/isAdmin'],
      rootGetters['session/isModerator'],
      rootGetters['session/isRep'],
    ].includes(true)
  },
  realtimeDriver(state) {
    return state.constants.registry.realtime_driver || 'legacy'
  },
  canOpenCustomFormModal(state, getters, rootState, rootGetters) {
    const { show_list_entries } = state.constants.registry
    const validRole = [
      rootGetters['session/isAdmin'],
      rootGetters['session/isModerator'],
    ].includes(true)
    return show_list_entries && validRole
  },
  virtualRoomCurrentElements(state, getters) {
    return state.show.data.elements
      .filter((row) =>
        row.breakpoints.split(',').includes(getters.currentBreakPointKey),
      )
      .map((row) => {
        if (row.element_type.name == ElementTypeKeys.slider) {
          const slider_item_slides = row.slider_item_slides.filter((row) => {
            if (row.duration) {
              return isHappening(row.duration)
            }
            return true
          })
          return { ...row, slider_item_slides }
        }

        return row
      })
  },
  emebedContentElement(_, getters) {
    const emebedVideoElement = getters.virtualRoomCurrentElements.find(
      (row) =>
        row.element_type.name == ElementTypeKeys.video &&
        row.video_item_type.name == VideoKeys.element,
    )
    if (!emebedVideoElement) {
      return null
    }
    return emebedVideoElement.content_element
  },
  plainMetricsPayload(state, getters) {
    const content_library_element_id = currentContentLibraryElementId(
      state,
      getters,
    )
    return {
      nav_count: state.constants.nav_count,
      uuid_tracker: state.constants.uuid_tracker,
      device_width: window.innerWidth,
      device_height: window.innerHeight,
      room_width: state.constants.room_width,
      room_height: state.constants.room_height,
      virtual_room_id: state.show.data.id,
      content_library_element_id,
    }
  },
  styles_element(state) {
    return state.show.data.elements.reduce((row, curr) => {
      const multiplier = state.constants.room_width / 1000
      row[curr.uuid] = {
        width: parseInt(curr.desktop_width) * multiplier + 'px',
        height: parseInt(curr.desktop_height) * multiplier + 'px',
        top: parseInt(curr.desktop_y) * multiplier + 'px',
        left: parseInt(curr.desktop_x) * multiplier + 'px',
      }
      return row
    }, {})
  },
  eventConfig(state) {
    return state.constants.event_configs.data
  },
  eventRegistryConfig(state) {
    return state.constants.registry.configs.find(
      (row) =>
        row.language.name ===
        state.constants.event_configs.data.current_config_language_key,
    )
  },
  currentForm(state) {
    if (isObjectEmpty(state.constants.registry)) {
      return {}
    }
    return state.constants.registry.forms.find(
      (row) =>
        row.language.name ===
        state.constants.event_configs.data.current_config_language_key,
    )
  },

  getRoomFromConstants: (state) => (id) => {
    return state.constants.virtual_rooms.find((room) => room.id === id)
  },
  // Sin console.log del array: con DevTools abierto, loguear ~1.100 objetos
  // reactivos en cada evaluación cuesta caro y los retiene en memoria.
  checkUser: (state) => (id) => {
    return state.socket.users.find((user) => user.id === id)
  },
  idsUsers: (state) => {
    return state.socket.users.map((user) => user.id)
  },
  myRepRooms: ({ constants }) => {
    return constants.my_rooms
  },
}

// actions
const actions = {
  async getRepRoomVisitors({ state }) {
    const slug = state.rep_dashboard.currentSelectedSlug
    const uuid = state.constants.registry.uuid
    return EventServiceInstance.getRepRoomVisitors(slug, uuid)
      .then((res) => {
        return res
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async openBussinessCard({ dispatch, commit }, { uuid = '' }) {
    const user = await dispatch('getUserByUuid', uuid)
    commit('setCard', { attendee: user, modal: true })
  },
  // ---- Lazy roster (users-list-plan.md) ----
  fetchRosterFirstPage(context) {
    return fetchRosterPage(context)
  },
  fetchRosterNextPage(context) {
    return fetchRosterPage(context, { append: true })
  },
  setRosterSearch(context, search) {
    const { commit, state } = context
    commit('setRoster', { search })
    // Invalidate immediately, including during the debounce window.
    commit('invalidateRoster')
    fetchRosterAfterSearch(context, state.roster.generation)
  },
  setRosterFilters({ commit, dispatch }, filters) {
    commit('setRoster', { filters })
    return dispatch('fetchRosterFirstPage')
  },
  // On-demand identity resolution: roster pages / cache first, then the server.
  async getUserByUuid({ state, commit }, uuid) {
    if (!uuid) {
      return null
    }
    const known =
      state.socket.users.find((row) => row.uuid === uuid) ||
      state.roster.items.find((row) => row.uuid === uuid) ||
      state.roster.userCache[uuid]
    if (known) {
      return known
    }
    // Resolve on demand; a uuid that isn't an event attendee 404s — swallow it so
    // realtime callers (DM toast, add-contact, clickables) just get null instead
    // of an unhandled rejection.
    try {
      const { data } = await EventServiceInstance.getEventUser(uuid)
      commit('cacheUser', data.user)
      return state.roster.userCache[data.user.uuid]
    } catch (e) {
      return null
    }
  },
  getEventUsers: debounce(function ({ commit, state }) {
    if (state.constants.users_loading) {
      console.log('stops')
      return
    }
    commit('setConstants', {
      users_loading: true,
    })
    return EventServiceInstance.getUsers()
      .then(({ data: { users } }) => {
        commit('setSocket', {
          users,
        })
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
      .finally(() => {
        commit('setConstants', {
          users_loading: false,
          needs_refershing: false,
        })
      })
  }, 2000),
  gestEventUsers({ commit, state }) {
    if (state.constants.users_loading) {
      console.log('stops')
      return
    }
    commit('setConstants', {
      users_loading: true,
    })
    return EventServiceInstance.getUsers()
      .then(({ data: { users } }) => {
        commit('setSocket', {
          users,
        })
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
      .finally(() => {
        commit('setConstants', {
          users_loading: false,
          needs_refershing: false,
        })
      })
  },
  getEventUsersHybrid({ commit, getters }) {
    // Defense in depth: the full hybrid roster is a privileged (ModAforo) concern;
    // regular attendees never load it (caveat 1).
    if (!getters.isPrivileged) {
      commit('setConstants', { needs_hybrid_refershing: false })
      return
    }
    return EventHybridServiceInstance.getUsersHybrid()
      .then(({ data: { users } }) => {
        commit('setSocket', { users })
      })
      .finally(() => {
        commit('setConstants', {
          needs_hybrid_refershing: false,
        })
      })
  },
  // ------ Contants ------ // 
  async loadConstants({ commit, state }) {
    // if (!state.constants.constantsLoaded) {
    commit('setConstants', { constantsLoaded: false })
    const { data } = await EventServiceInstance.init({
      params: {
        version: state.constants.is_preview ? 'rehersal' : 'live',
      },
    })
    const {
      registry,
      event_connections,
      event_users_block,
      profiles,
      favorites,
      rep_rooms,
      license,
      version,
      qrlink,
      threads,
      channels,
      notifications,
      quick_notes,
      support_user,
      location_counts,
    } = data
    commit('setConstants', {
      location_counts: location_counts ?? null,
      registry,
      event_connections,
      event_users_block,
      profiles,
      favorites,
      rep_rooms,
      license,
      version,
      qrlink,
      threads,
      channels,
      notifications,
      quick_notes,
      support_user,
    })
    if (rep_rooms.length > 0) {
      commit('setRepDashboard', {
        currentSelectedSlug: rep_rooms[0].slug,
      })
    }
    // Seed socket.users with the user's connections so "my contacts" resolves
    // without the full roster when the eager load is gated off (caveat 1).
    commit('mergeUsers', (event_connections || []).map((row) => row.event_user))
    commit('setConstants', { constantsLoaded: true })
  },
  // ------ Load Constant V2 ------ // 
  async loadContantsV2({dispatch, getters}) {
    const promises = [
      dispatch('loadEventConfigs'),
      dispatch('loadVirtualRooms'),
      dispatch('loadAttendeeCount'),
    ]
    // Only privileged staff (mod/rep dashboards) eager-load the full attendee
    // roster; regular attendees stay lazy and resolve users on demand (caveat 1).
    if (getters.isRealtimeEnabled && getters.isPrivileged) {
      promises.push(dispatch('loadUsersAction'))
    }
    return Promise.all(promises)
    .catch((err) => {
      console.log('🚀 ~ file: event.js:382 ~ loadContantsV2 ~ err:', err)
    })
  },
  // ------ Virtual Rooms ------ // 
  async loadVirtualRooms({ commit, state }) {
    const { data } = await EventServiceInstance.initVirtualRoom({
      params: { version: state.constants.is_preview ? 'rehersal' : 'live' },
    })
    const { virtual_rooms } = data
    commit('setConstants', { virtual_rooms })
  },
  // ------ Event Configs ------ // 
  async loadEventConfigs({ commit, state }) {
    const { data } = await EventServiceInstance.initEventConfigs({
      params: { version: state.constants.is_preview ? 'rehersal' : 'live' },
    })
    const { event_configs } = data
    event_configs.forEach((config_item) => {
      const formated_row = {[config_item.config_language.name]: config_item}
      commit('setNavItems', {
        current_config_language_key: config_item.config_language.name,
        nav_items: config_item.nav_items,
      })
      commit('setConfig', formated_row)
    })
  },
  async loadUsersAction({ commit, state }) {
    const { data } = await EventServiceInstance.initUsersAction({
      params: { version: state.constants.is_preview ? 'rehersal' : 'live' },
    })
    const { users } = data
    commit('setSocket', { users })
    commit('setSocket', { usersLoaded: true })
  },
  async loadAttendeeCount({ commit, state }) {
    commit('invalidateAttendeeCount')
    const registryUuid = state.constants.registry.uuid
    const generation = state.constants.attendees_count_generation
    const isCurrent = () => state.constants.registry.uuid === registryUuid &&
      state.constants.attendees_count_generation === generation

    for (let attempt = 1; attempt <= ATTENDEE_COUNT_ATTEMPTS; attempt++) {
      if (!isCurrent()) return
      try {
        const { data } = await EventServiceInstance.getAttendeeCount()
        if (isCurrent()) commit('setConstants', { attendees_count: data.attendees_count })
        return
      } catch (err) {
        if (!isCurrent()) return
        const status = err.response && err.response.status
        const retryable = !status || status >= 500 || [408, 425, 429].includes(status)
        if (attempt === ATTENDEE_COUNT_ATTEMPTS || !retryable) throw err
        await new Promise((resolve) => setTimeout(resolve, ATTENDEE_COUNT_RETRY_MS))
      }
    }
  },
  // Lazily load the full attendee roster on first need (e.g. the appointment /
  // group-chat pickers) for non-privileged users who skipped the eager load.
  async ensureUsersLoaded({ state, dispatch }) {
    if (state.socket.usersLoaded) {
      return
    }
    return dispatch('loadUsersAction')
  },
  // On-demand resolve a single attendee by numeric id (the open_user_chat
  // clickable carries an id, not a uuid). Backed by /event/users/{id}.
  async getUserById({ state, commit }, id) {
    if (!id) {
      return null
    }
    const known = state.socket.users.find((row) => String(row.id) === String(id))
    if (known) {
      return known
    }
    try {
      const { data } = await EventServiceInstance.getEventUser(id)
      commit('cacheUser', data.user)
      return state.roster.userCache[data.user.uuid]
    } catch (e) {
      return null
    }
  },
  async show({ commit }, { id }) {
    // commit("setAppIsLoading", true, { root: true });

    commit('setShow', { isLoading: true })

    return EventServiceInstance.getRoom(id)
      .then(({ data }) => {
        commit('setShow', { data: data.data })
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
      .finally(() => {
        commit('setShow', { isLoading: false })
        // commit("setAppIsLoading", false, { root: true });
      })
  },
  async createFavorite({ commit }, body) {
    // const { slug } = state.constants.registry
    return EventServiceInstance.createFavorite(body)
      .then(({ data }) => {
        const { favorites } = data
        commit('setConstants', { favorites })
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async delteFavorite({ commit }, body) {
    const { favorite_id } = body
    return EventServiceInstance.deleteFavorite(favorite_id)
      .then(({ data }) => {
        const { favorites } = data
        commit('setConstants', { favorites })
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async createEventConnection({ state, commit }, body) {
    const { slug } = state.constants.registry
    return EventServiceInstance.createEventConnection(slug, body)
      .then(({ data }) => {
        const { event_connections } = data
        commit('setConstants', { event_connections })
        commit('mergeUsers', (event_connections || []).map((row) => row.event_user))
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async deleteEventConnection({ state, commit }, body) {
    const { slug } = state.constants.registry
    return EventServiceInstance.deleteEventConnection(slug, body)
      .then(({ data }) => {
        const { event_connections } = data
        commit('setConstants', { event_connections })
        commit('mergeUsers', (event_connections || []).map((row) => row.event_user))
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  // ------------------ Matchmaking ------------------ //
  getAutomaticMatch(__, payload) {
    return EventServiceInstance.getAutomaticMatch(payload)
      .then(({data}) => {
        return data
      })
  },
  getFilterMatch(__, payload) {
    return EventServiceInstance.getFilterMatch(payload)
      .then(({data}) => {
        return data
      })
  },
  // ------------------ Notifications ------------------ //
  async getNotifications({commit, state}) {
    const { forms } = state.constants.registry
    return EventNotificationsInstance.notifications({
      'forms': forms,
    })
    .then(({ data }) => {
      const { notifications } = data
      commit('setConstants', { notifications })
    })
  },
  async deleteNotification({commit, state}, body) {
    const { forms } = state.constants.registry
    return EventNotificationsInstance.deleteNotification(body.id, {
      'forms': forms,
    })
      .then(({ data }) => {
        const { notifications } = data
        commit('setConstants', { notifications })
      })
  },
  async deleteAll({commit, state}) {
    const { forms } = state.constants.registry
    return EventNotificationsInstance.deleteAll({
      'forms': forms,
    })
      .then(({ data }) => {
        const { notifications } = data
        commit('setConstants', { notifications })
      })
  },
  async updateMark({commit, state}, body) {
    const { forms } = state.constants.registry
    return EventNotificationsInstance.updateMark(body.id, {
      'forms': forms,
    })
      .then(({ data }) => {
        const { notifications } = data
        commit('setConstants', { notifications })
      })
  },
  async newNotificationMessage(_, body) {
    return EventNotificationsInstance.newMessage(body)
  },
  async broadcastAfterLogin(_, body) {
    return EventNotificationsInstance.broadcastAfter(body)
  },
  async missedCallNotification(_, body) {
    return EventNotificationsInstance.missedCall(body)
  },
  // ------------------ Quick Notes ------------------ //
  async quickNotesStore({ commit }, body) {
    return quickNotesServiceInstance.store(body)
    .then(({ data }) => {
      const { quick_notes } = data
      commit('setConstants', { quick_notes })
    })
  },
  async quickNotesDelete({commit}, body) {
    return quickNotesServiceInstance.delete(body)
    .then(({data}) => {
      const { quick_notes } = data
      commit('setConstants', { quick_notes })
    })
  },
  async quickNotesHighlight({commit}, body) {
    return quickNotesServiceInstance.highlight(body)
    .then(({data}) => {
      const { quick_notes } = data
      commit('setConstants', { quick_notes })
    })
  },
  async quickNoteSendEmail(__, body) {
    return quickNotesServiceInstance.email(body)
    .then(() => {
      console.time('🚀 send')
    })
  },
  // ------------------ Quick Notes ------------------ //
  // ------------------ Calendar Appointment ------------------ //
  async registryCalendarIndex({state}, { payload }) {
    const registry = state.constants.registry.id
    return RegistriesCalendarsService.index(registry, payload)
      .then(({data}) => {
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async destroyCalendarItems(_, {payload, id, calendar}) {
    return CalendarsItemsService.destroy(calendar, id, payload)
      .then(({ data }) => {
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async getRegistryCalendarRequests({ payload }) {
    return CalendarRequestService.requestIndex(payload)
    .then(({ data }) => {
      return data
    })
  },
  async getRegistryCalendarReceived({ payload }) {
    return CalendarRequestService.receivedIndex(payload)
    .then(({ data }) => {
      return data
    })
  },
  async acceptedReceivedCalendar(_, { body = {} }) {
    return CalendarRequestService.receivedAccepted(body)
    .then(({ data }) => {
      return data
    })
  },
  // ------------------ Calendar Appointment ------------------ //
  async accessControlStore({ state, commit }, { payload }) {
    const registry_id = state.constants.registry.id
    const form = {
      ...payload,
    }
    return AccessControlsServiceInstance.store(registry_id, form)
      .then(({ data }) => {
        // Acual usuario bloqueado 
        const userBlocked = data.data
        // Todos los usuarios bloqueados
        const rows = state.constants.event_users_block
        // Actualizar la lista
        const event_users_block = [
          ...rows,
          userBlocked,
        ]
        commit('setConstants', { event_users_block })
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async updateProfile({ commit }, { uuid = '', body = {} }) {
    commit('setUserProfile', { isUpdating: true })
    console.log(body)
    return EventServiceInstance.updateUserProfile(uuid, body)
      .then(({ data }) => {
        const {
          profiles,
          user: { email, first_name, last_name, avatar },
        } = data
        commit('setConstants', {
          profiles,
        })
        commit(
          'session/setUser',
          {
            email,
            first_name,
            last_name,
            avatar,
          },
          { root: true },
        )
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
      .finally(() => {
        commit('setUserProfile', { isUpdating: false })
      })
  },
  async updateProfileMod(__, { uuid = '', user_id = '' , body = {} }) {
    return EventServiceInstance.updateUserProfileMod(uuid, user_id, body)
    .then(() => {
      console.log('Informacion actualizada')
    })
    .catch((err) => {
      throw formErrorsHandler(err)
    })
  },
  // Clic en un click action, para el leaderboard (regla «Click en click
  // action»). Vive en el store y no en un import del componente a propósito:
  // RenderlessClickableHandle importando el servicio cerraba un círculo
  // (servicio → axios → store → session → router → vistas → el propio
  // componente) y Vue no lo registraba: los click actions dejaban de
  // responder. No se espera la respuesta; si falla, el click action sigue.
  recordClick(_, { clickable, virtualRoomId }) {
    if (!clickable || !clickable.id) {
      return
    }
    EventServiceInstance.recordClick({
      clickable_id: clickable.id,
      virtual_room_id: virtualRoomId || null,
      device_width: window.innerWidth,
      device_height: window.innerHeight,
    }).catch(() => {})
  },
  // Apertura de un contenido de la librería (lo que cuenta Elements breakdown
  // en métricas). Había tres copias del registro, cada una al FINAL de su
  // contentItemEvent: si algo fallaba antes de llegar —un download_item_url
  // vacío, por ejemplo— el contenido no quedaba registrado, y la copia del menú
  // móvil (EventContainer) no lo registraba nunca. Ahora todas despachan esto
  // ANTES de abrir el contenido.
  recordContentOpen({ getters, dispatch }, element) {
    if (!element || !element.id) {
      return
    }
    const core_vital_type = AppCoreVitalKeys.EventContent
    dispatch(
      'general/recordCoreVital',
      {
        payload: {
          ...getters.plainMetricsPayload,
          core_vital_type,
          simple_log: element.content_type ? element.content_type.name : null,
          content_library_element_id: element.id,
        },
        core_vital_type,
      },
      { root: true },
    )
  },
  async getEventLeaderboardStats() {
    return EventServiceInstance.getEventLeaderboardStats()
      .then(({ data }) => {
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async showPhotoboothConfig(
    { commit },
    { photo_booth_config = '', body = {} },
  ) {
    return EventServiceInstance.showPhotoboothConfig(photo_booth_config, body)
      .then(({ data: { data: photo_booth_config }, ...data }) => {
        commit('setPhotobooth', {
          photo_booth_config,
        })
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async indexPhotoboothConfigGallery(
    _,
    { photo_booth_config = '', body = {} },
  ) {
    return EventServiceInstance.indexPhotoboothConfigGallery(
      photo_booth_config,
      body,
    )
      .then((res) => {
        return res
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async storePhotoboothPhoto(_, { uuid = '', body = {} }) {
    return EventServiceInstance.storePhotoboothPhoto(uuid, body)
      .then(({ data }) => {
        return data
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  async indexPhotoboothGallery(_, { registry_id = '', body = {} }) {
    return EventServiceInstance.indexPhotoboothGallery(registry_id, body)
      .then((res) => {
        return res
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
  },
  updatePasswordProfile(_, { uuid = '', body = {} }) {
    // commit('setAppIsLoading', true, { root: true })
    return EventServiceInstance.storeNewPassword(uuid, body)
      .then((res) => res)
      .catch((err) => {
        throw formErrorsHandler(err)
      })
      .finally(() => {
        // commit('setAppIsLoading', false, { root: true })
      })
  },
  exportEventConnections({ state, commit }) {
    commit('setUserCards', { isExporting: true })
    const { uuid } = state.constants.registry
    return EventServiceInstance.exportEventConnection(uuid)
      .then((res) => {
        axiosDownloadable(res, 'tarjetas_')
      })
      .catch((err) => {
        throw formErrorsHandler(err)
      })
      .finally(() => {
        commit('setUserCards', { isExporting: false })
      })
  },
}

// mutations
const mutations = {
  setCustomForm(state, obj) {
    Object.keys(obj).forEach((k) => (state.customform[k] = obj[k]))
  },
  setNavItems(state, { nav_items, current_config_language_key }) {
    state.constants.nav_items.data[current_config_language_key] = nav_items
  },
  setConfig(state, obj) {
    Object.keys(obj).forEach(
      (k) => (state.constants.event_configs.data[k] = obj[k]),
    )
  },
  setConstants(state, obj) {
    Object.keys(obj).forEach((k) => (state.constants[k] = obj[k]))
  },
  invalidateAttendeeCount(state) {
    state.constants.attendees_count = null
    state.constants.attendees_count_generation++
  },
  setSocket(state, obj) {
    if (obj.users) {
      obj = { ...obj, users: obj.users.map((user) => withOnlinePresence(state, user)) }
    }
    if (JSON.stringify(obj.users) !== JSON.stringify(state.socket.users)) {
      Object.keys(obj).forEach((k) => (state.socket[k] = obj[k]))
    }
  },
  // ---- Lazy roster (users-list-plan.md) ----
  invalidateRoster(state) {
    state.roster.generation++
    state.roster.items = []
    state.roster.cursor = null
    state.roster.hasMore = false
    state.roster.loading = false
  },
  setRoster(state, obj) {
    if (obj.items) {
      obj = { ...obj, items: obj.items.map((user) => withOnlinePresence(state, user)) }
    }
    Object.keys(obj).forEach((k) => (state.roster[k] = obj[k]))
  },
  appendRoster(state, items) {
    state.roster.items = [...state.roster.items, ...items.map((user) => withOnlinePresence(state, user))]
  },
  // Full snapshot, also retained for pages/contacts loaded after `here` fires.
  replaceOnlinePresenceSnapshot(state, onlineIds) {
    state.socket.onlineUsers = Object.fromEntries(Array.from(onlineIds, (id) => [id, true]))
    refreshOnlinePresence(state)
  },
  // Join/leave deltas follow the initial snapshot. Object keys normalize string
  // and numeric IDs received from the WebSocket and API respectively.
  setUserOnlinePresence(state, { id, online }) {
    if (state.socket.onlineUsers === null) return
    // Vue.set/delete en vez de copiar el mapa: copiarlo obligaba a Vue a
    // volver a hacer reactiva cada clave (una por usuario conectado) en cada
    // evento.
    if (online) Vue.set(state.socket.onlineUsers, id, true)
    else Vue.delete(state.socket.onlineUsers, id)
    refreshUserPresence(state, id)
  },
  clearOnlinePresence(state) {
    state.socket.onlineUsers = null
  },
  cacheUser(state, user) {
    if (user && user.uuid) {
      user = withOnlinePresence(state, user)
      state.roster.userCache = { ...state.roster.userCache, [user.uuid]: user }
      // Grow socket.users so on-demand-resolved attendees (DM partners, clicked
      // users) persist for the consumers that still read the array (ChatHome's
      // active-chat list, business cards). Deduped; a no-op once the full roster
      // is loaded. Also a prerequisite for gating the eager load (caveat 1).
      if (!state.socket.users.some((row) => row.id === user.id)) {
        state.socket.users = [...state.socket.users, user]
      }
    }
  },
  // Merge a bounded set of attendee objects into socket.users (deduped) — seeds
  // the array from chat participants + connections so the bounded consumers
  // (ChatHome active list, my-contacts) work without the full eager load (caveat 1).
  mergeUsers(state, users) {
    const incoming = (users || []).filter((u) => u && u.id != null)
      .map((user) => withOnlinePresence(state, user))
    if (!incoming.length) {
      return
    }
    const known = new Set(state.socket.users.map((row) => row.id))
    const additions = incoming.filter((u) => !known.has(u.id))
    if (additions.length) {
      state.socket.users = [...state.socket.users, ...additions]
    }
  },
  setShow(state, obj) {
    Object.keys(obj).forEach((k) => (state.show[k] = obj[k]))
  },
  setFlexible(state, obj) {
    Object.keys(obj).forEach((k) => (state.flexible[k] = obj[k]))
  },
  setUserProfile(state, obj) {
    Object.keys(obj).forEach((k) => (state.userProfile[k] = obj[k]))
  },
  setUserCards(state, obj) {
    Object.keys(obj).forEach((k) => (state.userCards[k] = obj[k]))
  },
  setCard(state, obj) {
    Object.keys(obj).forEach((k) => (state.card[k] = obj[k]))
  },
  setPhotobooth(state, obj) {
    Object.keys(obj).forEach((k) => (state.photobooth[k] = obj[k]))
  },
  setProfile(state, obj) {
    Object.keys(obj).forEach((k) => (state.profile[k] = obj[k]))
  },
  setRepDashboard(state, obj) {
    Object.keys(obj).forEach((k) => (state.rep_dashboard[k] = obj[k]))
  },
  setModDashboard(state, obj) {
    Object.keys(obj).forEach((k) => (state.mod_dashboard[k] = obj[k]))
  },
  setModDashboardCustomForm(state, obj) {
    Object.keys(obj).forEach((k) => (state.mod_dashboard_custom_form[k] = obj[k]))
  },
  setSearch(state, obj) {
    Object.keys(obj).forEach((k) => (state.search[k] = obj[k]))
  },
  setIframeItem(state, obj) {
    Object.keys(obj).forEach((k) => (state.iframeItem[k] = obj[k]))
  },
  setContentItem(state, obj) {
    Object.keys(obj).forEach((k) => (state.contentItem[k] = obj[k]))
  },
  setContentVideo(state, obj) {
    Object.keys(obj).forEach((k) => (state.contentVideo[k] = obj[k]))
  },
  setActiveInmediatePopUp(state, obj) {
    Object.keys(obj).forEach((k) => (state.activeInmediatePopUp[k] = obj[k]))
  },
  setContentWindow(state, obj) {
    Object.keys(obj).forEach((k) => (state.contentWindow[k] = obj[k]))
  },
  setSimpleVideoItem(state, obj) {
    Object.keys(obj).forEach((k) => (state.simpleVideoItem[k] = obj[k]))
  },
}

export const Event = {
  namespaced: true,
  state: initState,
  getters,
  actions,
  mutations,
}
